import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ShieldCheck,
    Package,
    Truck,
    Send,
    FileBarChart,
    Download,
    Settings,
    MoreVertical,
    ChevronDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { cn } from '../../components/ui/utils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import InventoryDashboard from './InventoryDashboard';
import InventoryList from './InventoryList';
import IncomingInventory from './IncomingInventory';
import InventoryIssuance from './InventoryIssuance';
import InventorySettings from './InventorySettings';
import InventoryReports from './InventoryReports';

const InventoryManagement: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [reportView, setReportView] = useState<'main' | 'worker' | 'item' | 'location' | 'cost' | 'replacement'>('main');
    const navigate = useNavigate();

    const tabs = [
        { id: 'dashboard', label: 'Dashboard', icon: <FileBarChart size={18} />, action: ACTIONS.READ, subject: RESOURCES.DASHBOARD },
        { id: 'inventory', label: 'Inventory', icon: <Package size={18} />, action: ACTIONS.READ, subject: RESOURCES.INVENTORY_ITEM },
        { id: 'incoming', label: 'Incoming', icon: <Truck size={18} />, action: ACTIONS.READ, subject: RESOURCES.INCOMING_INVENTORY },
        { id: 'issuance', label: 'Issuance', icon: <Send size={18} />, action: ACTIONS.READ, subject: RESOURCES.INVENTORY_ISSUANCE },
        { id: 'reports', label: 'Reports', icon: <FileBarChart size={18} />, action: ACTIONS.READ, subject: RESOURCES.REPORT },
    ].filter(tab => ability.can(tab.action, tab.subject));

    return (
        <div className="flex flex-col space-y-6 animate-in fade-in duration-500">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Uniform & PPE Management</h1>
                    <p className="text-gray-500">
                        Manage inventory, track issuances, and maintain stock levels across all locations
                    </p>
                </div>
                {/* Desktop Actions */}
                <div className="hidden md:flex items-center gap-3">
                    {ability.can(ACTIONS.READ, RESOURCES.INVENTORY_SETTING) && (
                        <Button
                            variant="outline"
                            onClick={() => setActiveTab('settings')}
                            className={cn(
                                "flex items-center gap-2 transition-all text-sm",
                                activeTab === 'settings' ? "bg-slate-100 border-slate-300 ring-1 ring-slate-200" : ""
                            )}
                        >
                            <Settings size={16} />
                            Settings
                        </Button>
                    )}
                    {ability.can(ACTIONS.CREATE, RESOURCES.INVENTORY_ISSUANCE) && (
                        <Button
                            onClick={() => navigate('/inventory/issuances/new')}
                            className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white border-0 shadow-sm transition-all hover:scale-105 active:scale-95 text-sm"
                        >
                            <Send size={16} />
                            New Issuance
                        </Button>
                    )}
                </div>

                {/* Mobile Actions Dropdown */}
                <div className="md:hidden">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="flex items-center gap-2 w-full justify-center">
                                <MoreVertical size={18} />
                                Actions
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            {ability.can(ACTIONS.READ, RESOURCES.INVENTORY_SETTING) && (
                                <DropdownMenuItem
                                    className="py-3"
                                    onClick={() => setActiveTab('settings')}
                                >
                                    <Settings size={18} className="mr-2" />
                                    <span>Settings</span>
                                </DropdownMenuItem>
                            )}
                            {ability.can(ACTIONS.CREATE, RESOURCES.INVENTORY_ISSUANCE) && (
                                <DropdownMenuItem
                                    className="py-3 bg-slate-900 text-white m-1 hover:bg-slate-800 focus:bg-slate-800 focus:text-white"
                                    onClick={() => navigate('/inventory/issuances/new')}
                                >
                                    <Send size={18} className="mr-2" />
                                    <span>New Issuance</span>
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* Tab Navigation */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                {/* Desktop Tab List */}
                <div className="hidden md:block w-full overflow-x-auto pb-1 no-scrollbar">
                    <TabsList className="bg-gray-100 p-1 rounded-full min-w-max md:w-fit flex items-center h-auto">
                        {tabs.map((tab) => (
                            <TabsTrigger
                                key={tab.id}
                                value={tab.id}
                                className="rounded-full px-4 md:px-6 py-2 flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-blue-600 font-medium transition-all whitespace-nowrap"
                            >
                                {tab.icon}
                                <span className="text-sm md:text-base">{tab.label}</span>
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </div>

                {/* Mobile Tab Dropdown */}
                <div className="md:hidden w-full">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="w-full justify-between bg-gray-50 border-gray-200">
                                <div className="flex items-center gap-2">
                                    {tabs.find(t => t.id === activeTab)?.icon}
                                    <span className="font-medium text-gray-900">
                                        {tabs.find(t => t.id === activeTab)?.label || 'Menu'}
                                    </span>
                                </div>
                                <ChevronDown size={18} className="text-gray-500" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-[calc(100vw-3rem)]">
                            {tabs.map((tab) => (
                                <DropdownMenuItem
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={cn(
                                        "py-3 flex items-center gap-3",
                                        activeTab === tab.id ? "bg-blue-50 text-blue-600 focus:bg-blue-50 focus:text-blue-600" : ""
                                    )}
                                >
                                    <div className={cn(
                                        "transition-colors",
                                        activeTab === tab.id ? "text-blue-600" : "text-gray-500"
                                    )}>
                                        {tab.icon}
                                    </div>
                                    <span className="font-medium">{tab.label}</span>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {/* Content Area */}
                <div className="pt-6">
                    <TabsContent value="dashboard">
                        <InventoryDashboard onNavigateReports={(view: 'main' | 'worker' | 'item' | 'location' | 'cost' | 'replacement') => {
                            setReportView(view);
                            setActiveTab('reports');
                        }} />
                    </TabsContent>
                    <TabsContent value="inventory">
                        <InventoryList />
                    </TabsContent>
                    <TabsContent value="incoming">
                        <IncomingInventory />
                    </TabsContent>
                    <TabsContent value="issuance">
                        <InventoryIssuance />
                    </TabsContent>
                    <TabsContent value="reports">
                        <InventoryReports
                            initialView={reportView}
                        // Reset report view when it changes internally if needed, 
                        // but InventoryReports manages its own 'view' state now.
                        />
                    </TabsContent>
                    <TabsContent value="settings">
                        <InventorySettings onNavigate={(view) => {
                            console.log('Navigate to settings view:', view);
                            // This will be used to switch to sub-settings pages like Locations, etc.
                        }} />
                    </TabsContent>
                </div>
            </Tabs>


        </div>
    );
};

export default InventoryManagement;